import { useStore } from '@nanostores/react'
import { Sparkles } from 'lucide-react'

import { $user } from '~/features/auth/stores/user'
import { usePlatformSettings } from '~/features/auth/hooks/useAuthQueries'
import { useCurrentWorkspacePlan } from '~/features/billing/hooks/useBillingHooks'
import { formatDate } from '~/features/billing/utils'
import { getRoutePath } from '~/lib/router'
import { cn } from '~/lib/utils'

import { METERS, UsageBar, meterTone } from './UsageMeters'

/**
 * Compact usage meter for the sidebar footer: storage, Smart Searches and embedding
 * tokens against the plan allowance. Hidden for self-hosted instances and non-owners.
 */
export function UsageSidebarMeter() {
  const currentUser = useStore($user)
  const { data: platformSettings } = usePlatformSettings()
  const { state, name } = useCurrentWorkspacePlan()

  const isOwner = currentUser.currentScope?.role === 'owner'

  if (platformSettings?.selfHosted || !isOwner || !state) return null

  const isFree = state.plan === 'free'
  const billingHref = getRoutePath('workspaceBilling')
  const cancelAt = state.scheduledChange?.action === 'cancel' ? state.scheduledChange.effectiveAt : null

  return (
    <div className="flex flex-col gap-2.5 rounded-md border border-content/20 bg-fill p-4">
      <a className="flex flex-col gap-2" href={getRoutePath('workspaceApiUsage')}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium tracking-wide text-content2 uppercase">Usage</span>
          <span className="text-xs text-content3">{name}</span>
        </div>
        {METERS.map(({ key, label, format }) => {
          const meter = state.usage[key]
          const tone = meterTone(meter, isFree)
          return (
            <div
              className="flex flex-col gap-1"
              key={key}
              title={`${label}: ${format(meter.used)} / ${format(meter.included)}`}
            >
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="text-content3">{label}</span>
                <span
                  className={cn(
                    'font-mono text-content',
                    tone === 'danger' && 'text-danger',
                    tone === 'warning' && 'text-warning'
                  )}
                >
                  {format(meter.used)}&thinsp;/&thinsp;{format(meter.included)}
                </span>
              </div>
              <UsageBar className="h-1" hardStop={isFree} meter={meter} />
            </div>
          )
        })}
      </a>

      {isFree ?
        <a
          className="-mx-1 mt-0.5 -mb-1 flex items-center gap-2 rounded-md border border-accent/30 bg-accent/5 px-2.5 py-2 transition hover:bg-accent/10"
          href={billingHref}
        >
          <Sparkles className="h-4 w-4 shrink-0 text-accent" />
          <span className="text-xs leading-snug text-content2">
            <span className="font-medium text-content">Upgrade</span> for more storage and AI actions.
          </span>
        </a>
      : cancelAt ?
        <a className="text-xs text-content2 transition hover:text-content" href={billingHref}>
          Subscription ends {formatDate(cancelAt)}
        </a>
      : state.period ?
        <a className="text-xs text-content3 transition hover:text-content" href={billingHref}>
          Window resets {formatDate(state.period.end)}
        </a>
      : null}
    </div>
  )
}
